import React, { useEffect, useRef, useState } from 'react';
import { Button, showNotification } from 'react-admin';
import { connect } from 'react-redux';
import config from '../config';
import { scanError, scanSuccess, setLoading, setOpenQrScanner } from './virtualStationActions';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import QRScanner from './QRScanner';
import Loader from '../loader/Loader';
import './VirtualStation.css';
import { utf8ToHex } from '../utils';

const env = process.env.REACT_APP_CUSTOM_NODE_ENV;
const BASE_URL = config[env].url;
const dnoRegex = /^[0-9a-f]{16}$/;

const ScanDeviceButton = ({
    record,
    resource,
    permissions,
    showNotification,
    setLoading,
    scanSuccess,
    scanError,
    isLoading,
    openQrScanner,
    setOpenQrScanner,
}) => {
    const [showQrScanner, setShowQrScanner] = useState(false);
    const [stopStream, setStopStream] = useState(false);
    const [showScannerBox, setShowScannerBox] = useState(true);
    const abortControllerRef = useRef(null);

    const getApiBaseUrl = () => {
        return permissions === 'promoter'
            ? `${BASE_URL}promoter/`
            : `${BASE_URL}admin/`;
    };

    // Handle opening QR scanner when the Redux state changes
    useEffect(() => {
        if (openQrScanner && record && record.sno) {
            setShowQrScanner(true);
            // Reset the Redux state after opening the scanner
            setOpenQrScanner(false);
        }
    }, [openQrScanner, record, setOpenQrScanner]);

    const handleCloseQrScanner = () => {
        if (!abortControllerRef.current) {
            setStopStream(true);
            setTimeout(() => {
                setShowQrScanner(false);
                setTimeout(() => setShowScannerBox(true), 0);
            }, 0);
        }
    };

    const handleScan = async (data) => {
        if (data) {
            const prefix = utf8ToHex(data.slice(0, 4).toUpperCase()).toLowerCase()
            const tail = data.slice(4).toLowerCase();
            const dno = `${prefix}${tail}`
            if (dno && dnoRegex.test(dno)) {
                setStopStream(true);
                setTimeout(() => setShowQrScanner(false), 0);
                scanSuccess(dno);
                // Call the getDevice API
                if (record && resource) {
                    sendGetDeviceRequest(record.sno, dno).then(() => handleCloseQrScanner());
                } else {
                    showNotification(`Please retry`, 'warning');
                    handleCloseQrScanner();
                }
            } else {
                showNotification(`Scanned data not DNO (${data})`, 'warning');
                handleCloseQrScanner();
            }
        }
    };

    const handleScanError = (error) => {
        scanError(error);
        if (showScannerBox) {
            if (
                error ===
                'Error getting userMedia, error = NotFoundError: The object can not be found here.'
            ) {
                setShowScannerBox(false);
                showNotification('No camera found, enter DNO manually', 'info');
            } else {
                showNotification(`Scan error: ${error.message || error}`, 'warning');
            }
        }
    };

    // Action to handle the API call
    const sendGetDeviceRequest = async (sno, dno) => {
        if (!resource || !sno || !dno) {
            return;
        }
        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        const apiUrl = getApiBaseUrl();
        const url = `${apiUrl}${resource}/getDevice`;
        const token = localStorage.getItem('token');
        setLoading(true);
        return fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ sno, dno }),
            signal: abortControllerRef.current.signal,
        })
            .then(response => response.json().then(data => [response, data]))
            .then(([response, data]) => {
                if (response.status === 200) {
                    // Dispatch success action
                    showNotification(`Session with ID=${data.session.id} has started`, 'info');
                } else {
                    // Dispatch error action
                    showNotification(data?.message || 'Error occurred', 'warning');
                }
            })
            .catch(error => {
                // Dispatch error action
                showNotification(error.message || 'Network error occurred', 'warning');
            })
            .finally(() => {
                setLoading(false);
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
                abortControllerRef.current = null;
            });
    };

    return (
        <>
            {showQrScanner && (
                <Dialog open={showQrScanner} maxWidth="md" onClose={handleCloseQrScanner}>
                    <DialogTitle>
                        Scan device barcode
                        <IconButton
                            style={{ position: 'absolute', right: 8, top: 8 }}
                            onClick={handleCloseQrScanner}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={{ textAlign: 'center' }}>
                        <div style={{ margin: '10px 0' }}>
                            <input
                                type="text"
                                placeholder="Enter DNO manually"
                                style={{
                                    padding: '8px',
                                    marginRight: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    width: '200px'
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleScan(e.target.value);
                                    }
                                }}
                            />
                            <button
                                onClick={(e) => {
                                    const input = e.target.previousElementSibling;
                                    if (input.value)
                                    {handleScan(input.value);}
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Send
                            </button>
                        </div>
                        {showScannerBox &&
                            <QRScanner
                                onDecode={handleScan}
                                onError={handleScanError}
                                stopStream={stopStream}
                                style={{ width: '100%' }}
                            />
                        }
                    </DialogContent>
                </Dialog>
            )}
            {isLoading && (
                <div className='loader-container'>
                    <Loader/>
                </div>
            )}
        </>
    );
};

const mapStateToProps = state => ({
    isLoading: state.virtualStation.isLoading,
    openQrScanner: state.virtualStation.openQrScanner,
});

const mapDispatchToProps = dispatch => ({
    showNotification: (message, type) => dispatch(showNotification(message, type)),
    setLoading: (isLoading) => dispatch(setLoading(isLoading)),
    scanSuccess: (data) => dispatch(scanSuccess(data)),
    scanError: (error) => dispatch(scanError(error)),
    setOpenQrScanner: (open) => dispatch(setOpenQrScanner(open)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ScanDeviceButton);
